/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Module Management Service
 * ============================================================================
 *
 * Service layer for SuperAdmin module management APIs.
 * Handles module listing, activation status, and dependency management.
 *
 * ============================================================================
 */

import { get, post, del } from '@/services/api/client';
import { ApiResponse } from '@/services/api/types';

const BASE_URL = '/superadmin/modules';

// ============================================================================
// Types
// ============================================================================

export interface ModuleDependency {
  moduleCode?: string;
  dependsOnCode: string;
  isRequired: boolean;
  minTierCode?: string;
  description?: string;
}

export interface ModuleManagement {
  code: string;
  name: string;
  version: string;
  description: string;
  icon?: string;
  tablePrefix?: string;
  apiBasePath?: string;
  isRequired: boolean;
  displayOrder: number;
  features: string[];
  isActive: boolean;
  isPublic: boolean;
  tierCount: number;
  dependencies: ModuleDependency[];
  dependentModules: string[];
}

export interface CreateDependencyRequest {
  moduleCode: string;
  dependsOnCode: string;
  isHardDependency?: boolean;
  minTierCode?: string;
  description?: string;
}

// ============================================================================
// Service
// ============================================================================

export const modulesService = {
  /**
   * Get all modules with their metadata, dependencies, and status.
   */
  async getAllModules(): Promise<ModuleManagement[]> {
    const response = await get<ApiResponse<ModuleManagement[]>>(BASE_URL);
    return response.data;
  },

  /**
   * Get a single module's details.
   */
  async getModule(moduleCode: string): Promise<ModuleManagement> {
    const response = await get<ApiResponse<ModuleManagement>>(`${BASE_URL}/${moduleCode}`);
    return response.data;
  },

  /**
   * Get the full dependency graph.
   */
  async getDependencyGraph(): Promise<Record<string, ModuleDependency[]>> {
    const response = await get<ApiResponse<Record<string, ModuleDependency[]>>>(`${BASE_URL}/dependencies`);
    return response.data;
  },

  /**
   * Create a new dependency relationship.
   */
  async createDependency(request: CreateDependencyRequest): Promise<ModuleDependency> {
    const response = await post<ApiResponse<ModuleDependency>>(`${BASE_URL}/dependencies`, request);
    return response.data;
  },

  /**
   * Delete a dependency relationship.
   */
  async deleteDependency(moduleCode: string, dependsOnCode: string): Promise<void> {
    await del<ApiResponse<void>>(`${BASE_URL}/dependencies/${moduleCode}/${dependsOnCode}`);
  },

  /**
   * Get dependencies for a specific module.
   */
  async getModuleDependencies(moduleCode: string): Promise<ModuleDependency[]> {
    const response = await get<ApiResponse<ModuleDependency[]>>(`${BASE_URL}/${moduleCode}/dependencies`);
    return response.data;
  },

  /**
   * Get modules that depend on a specific module.
   */
  async getDependentModules(moduleCode: string): Promise<string[]> {
    const response = await get<ApiResponse<string[]>>(`${BASE_URL}/${moduleCode}/dependents`);
    return response.data;
  },
};

export default modulesService;
